// pages/DwiSendRequests.jsx
// DwiSend requests for DRC clients (USD) — the Demandes tab of /dwisend-drc
// (old /dwisend-requests links forward there) — in French or
// English (frontend/src/i18n/dwisend.js). A REQUEST, not a purchase:
//   send request → DwiSend reviews and quotes → client accepts → pays offline
//   (instructions shown only after accepting) → two DwiSend people check the
//   money → paid → on its way → delivered. Or DwiSend declines with a reason.
// The server decides who may use it (DRC account + dwisend_requests switch).
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { dwisendRequestAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Button, Card, Input, Select, Badge, Modal, Empty, Spinner } from '../components/ui';
import { t, money, date, langFor, LANGS } from '../i18n/dwisend';

const STATUS_COLOR = {
  submitted: 'amber', reviewing: 'cyan', quoted: 'green', awaiting_payment: 'amber', paid: 'teal',
  dispatched: 'cyan', delivered: 'green', declined: 'gray', cancelled: 'gray',
};
const QUOTE_COLOR = { sent: 'green', accepted: 'teal', rejected: 'gray', expired: 'gray' };
const textarea = 'mt-1 w-full bg-bg-elevated border border-border rounded-xl px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan';

// embedded: shown as the Demandes tab of the DwiSend page (pages/DwiSendHub.jsx),
// which owns the title and the language switch.
export default function DwiSendRequests({ embedded = false, lang: hubLang } = {}) {
  const { user, updateUser } = useAuth();
  const [ownLang, setLang]  = useState(() => langFor(user));
  const lang = hubLang || ownLang;
  const [data, setData]     = useState(null);
  const [closed, setClosed] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const T = (k, v) => t(lang, k, v);

  const load = useCallback(async () => {
    try { setData(await dwisendRequestAPI.mine()); setClosed(false); }
    catch (err) { if (err.status === 403) setClosed(true); else toast.error(err.message); }
  }, []);
  useEffect(() => { load(); }, [load, hubLang]);   // the hub's language switch: reload the texts in that language

  const switchLang = async l => {
    setLang(l);
    updateUser({ language: l });
    dwisendRequestAPI.setLanguage(l).then(load).catch(() => {});
  };

  const header = embedded ? null : (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-xl font-black text-text-primary">{T('req.title')}</h1>
        <p className="text-sm text-text-secondary mt-1 max-w-2xl">{T('req.subtitle')}</p>
      </div>
      <div className="flex items-center gap-1 text-xs" aria-label={T('req.lang')}>
        {LANGS.map(l => (
          <button key={l} onClick={() => switchLang(l)}
            className={`px-2.5 py-1 rounded-lg font-bold ${lang === l ? 'bg-cyan/20 text-cyan' : 'text-text-muted hover:text-text-primary'}`}>
            {l === 'fr' ? 'Français' : 'English'}
          </button>
        ))}
      </div>
    </div>
  );

  if (closed) {
    return <div className="space-y-4">{header}<Card><Empty icon="📝" title={T('req.closed.title')} description={T('req.closed.desc')} /></Card></div>;
  }
  if (!data) return <div className="py-16 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-4">
      {header}
      <Card>
        <ol className="grid gap-2 sm:grid-cols-5 text-xs text-text-secondary">
          {[1, 2, 3, 4, 5].map(i => (
            <li key={i} className="flex gap-2 items-start">
              <span className="shrink-0 w-5 h-5 rounded-full bg-cyan/15 text-cyan font-bold flex items-center justify-center">{i}</span>
              <span>{T(`req.steps.${i}`)}</span>
            </li>
          ))}
        </ol>
      </Card>
      {showForm
        ? <RequestForm lang={lang} data={data} onCancel={() => setShowForm(false)} onSent={() => { setShowForm(false); load(); }} />
        : <div><Button onClick={() => setShowForm(true)}>{T('req.new')}</Button></div>}
      {data.requests.length === 0
        ? !showForm && <Card><Empty icon="📝" title={T('req.empty.title')} description={T('req.empty.desc')} /></Card>
        : data.requests.map(r => <RequestCard key={r._id} r={r} lang={lang} whatsapp={data.whatsapp} onChanged={load} />)}
    </div>
  );
}

function RequestForm({ lang, data, onCancel, onSent }) {
  const { user } = useAuth();
  const { options, currency } = data;
  const T = (k, v) => t(lang, k, v);
  const [f, setF] = useState(() => ({
    productName: '', categoryId: options.categories[0]?._id || '', unitId: options.units[0]?._id || '',
    quantity: '', targetPrice: '', neededBy: '', cityId: '', deliveryAddress: '',
    phone: user?.phone || '', whatsapp: '', notes: '',
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const unit = options.units.find(u => u._id === f.unitId)?.name || '';

  const send = async () => {
    setSaving(true);
    try {
      // The target price goes as typed text ("42,50"); the server turns it into exact cents.
      await dwisendRequestAPI.create({ ...f, quantity: Number(f.quantity), neededBy: f.neededBy || null });
      toast.success(T('req.form.sent'));
      onSent();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (!options.cities.length) return <Card><Empty icon="🏙️" title={T('req.form.no_city')} /></Card>;

  return (
    <Card>
      <div className="text-text-primary font-bold mb-3">{T('req.form.title')}</div>
      <div className="space-y-3">
        <Input label={T('req.form.product')} value={f.productName} onChange={e => set('productName', e.target.value)} placeholder={T('req.form.product.ph')} />
        <div className="grid sm:grid-cols-2 gap-3">
          <Select label={T('req.form.category')} value={f.categoryId} onChange={e => set('categoryId', e.target.value)}>
            {options.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <Select label={T('req.form.unit')} value={f.unitId} onChange={e => set('unitId', e.target.value)}>
            {options.units.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </Select>
          <Input label={T('req.form.quantity', { unit })} type="number" min="1" step="1" value={f.quantity} onChange={e => set('quantity', e.target.value)} />
          <Input label={T('req.form.target', { unit, currency })} inputMode="decimal" value={f.targetPrice} onChange={e => set('targetPrice', e.target.value)} placeholder={T('req.form.target.ph')} />
          <Input label={T('req.form.needed')} type="date" value={f.neededBy} onChange={e => set('neededBy', e.target.value)} />
          <Select label={T('req.form.city')} value={f.cityId} onChange={e => set('cityId', e.target.value)}>
            <option value="">{T('req.form.city.choose')}</option>
            {options.cities.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-text-secondary">{T('req.form.address')}</label>
          <textarea rows={2} maxLength={500} value={f.deliveryAddress} onChange={e => set('deliveryAddress', e.target.value)} placeholder={T('req.form.address.ph')} className={textarea} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input label={T('req.form.phone')} value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="+243 8X XXX XXXX" />
          <Input label={T('req.form.whatsapp')} value={f.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="243XXXXXXXXX" />
        </div>
        <Input label={T('req.form.notes')} value={f.notes} onChange={e => set('notes', e.target.value)} placeholder={T('req.form.notes.ph')} />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>{T('req.form.cancel')}</Button>
          <Button loading={saving} onClick={send}>{T('req.form.send')}</Button>
        </div>
      </div>
    </Card>
  );
}

function RequestCard({ r, lang, whatsapp, onChanged }) {
  const T = (k, v) => t(lang, k, v);
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(T('req.card.cancel.confirm', { product: r.productName }))) return;
    setBusy(true);
    try { await dwisendRequestAPI.cancel(r._id); toast.success(T('req.card.cancelled')); onChanged(); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-text-primary font-bold">{r.productName}</div>
          <div className="text-[11px] text-text-muted">{r.ref} · {r.categoryName} · {T('req.card.asked', { date: date(r.createdAt, lang) })}</div>
        </div>
        <Badge label={T(`req.status.${r.status}`)} color={STATUS_COLOR[r.status] || 'gray'} />
      </div>
      <div className="text-sm mt-2">
        {r.quantity.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US')} × {r.unitName}
        {r.targetPriceMinor ? <> · {T('req.card.your_price', { amount: money(r.targetPriceMinor, r.currency, lang) })}</> : null}
        {r.neededBy ? <> · {T('req.card.by', { date: date(r.neededBy, lang) })}</> : null}
      </div>
      <div className="text-[11px] text-text-muted">{r.cityName} · {r.deliveryAddress}</div>
      {r.status === 'declined' && r.declineReason && (
        <div className="text-sm mt-2 rounded-lg bg-bg-elevated px-3 py-2"><span className="text-text-muted">{T('req.card.declined')}</span> {r.declineReason}</div>
      )}
      {r.payment && <PaymentBox r={r} lang={lang} />}
      {r.paymentStage === 'confirmed' && <div className="text-sm text-green mt-2">✓ {T('req.pay.confirmed')}</div>}
      {r.quotes.map(q => <QuoteBox key={q._id} q={q} lang={lang} onChanged={onChanged} />)}
      <div className="flex gap-2 flex-wrap mt-3">
        {r.canCancel && <Button size="xs" variant="ghost" loading={busy} onClick={cancel}>{T('req.card.cancel')}</Button>}
        {whatsapp && (
          <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(T('req.card.whatsapp.msg', { ref: r.ref, product: r.productName }))}`} target="_blank" rel="noopener noreferrer">
            <Button size="xs" variant="success">{T('req.card.whatsapp')}</Button>
          </a>
        )}
      </div>
    </Card>
  );
}

function PaymentBox({ r, lang }) {
  const T = (k, v) => t(lang, k, v);
  const p = r.payment;
  if (r.paymentStage === 'checking') {
    return <div className="mt-3 rounded-xl border border-cyan/40 bg-cyan/5 p-3 text-sm">{T('req.pay.checking')}</div>;
  }
  return (
    <div className="mt-3 rounded-xl border border-amber/50 bg-amber/5 p-3 space-y-2">
      <div className="text-sm font-bold text-text-primary">{T('req.pay.title')}</div>
      <div className="grid sm:grid-cols-2 gap-2 text-sm">
        <div><div className="text-[11px] text-text-muted">{T('req.pay.amount')}</div><div className="font-bold text-text-primary">{money(p.amountMinor, p.currency, lang)}</div></div>
        <div><div className="text-[11px] text-text-muted">{T('req.pay.reference')}</div><div className="font-mono font-bold text-cyan">{p.reference}</div></div>
      </div>
      {p.instructions.length
        ? <ul className="text-sm space-y-1">{p.instructions.map((line, i) => <li key={i} className="rounded-lg bg-bg-elevated px-3 py-1.5 select-all">{line}</li>)}</ul>
        : <div className="text-sm text-text-secondary">{T('req.pay.no_details')}</div>}
      <div className="text-xs text-text-muted">{T('req.pay.waiting_note')}</div>
    </div>
  );
}

function QuoteBox({ q, lang, onChanged }) {
  const T = (k, v) => t(lang, k, v);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const live = q.status === 'sent';
  const m = minor => money(minor, q.currency, lang);
  return (
    <div className={`mt-3 rounded-xl border p-3 ${live ? 'border-green/50 bg-green/5' : 'border-border'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold text-text-primary">{T('req.quote.title', { ref: q.ref })}</div>
        <Badge label={T(`req.quote.status.${q.status}`)} color={QUOTE_COLOR[q.status] || 'gray'} />
      </div>
      <div className="mt-2 space-y-0.5 text-sm">
        {q.lines.map(l => <div key={l._id} className="flex justify-between gap-3"><span>{l.qty.toLocaleString()} × {l.name} ({l.unitName}) @ {m(l.unitPriceMinor)}</span><span>{m(l.lineTotalMinor)}</span></div>)}
        {q.deliveryFeeMinor > 0 && <div className="flex justify-between gap-3 text-text-muted"><span>{T('req.quote.delivery')}</span><span>{m(q.deliveryFeeMinor)}</span></div>}
        <div className="flex justify-between gap-3 font-bold text-text-primary border-t border-border pt-1"><span>{T('req.quote.total')}</span><span>{m(q.totalMinor)}</span></div>
      </div>
      {q.terms && <div className="text-xs text-text-secondary mt-2 whitespace-pre-line">{T('req.quote.terms')} {q.terms}</div>}
      <div className="text-[11px] text-text-muted mt-1">
        {live ? T('req.quote.valid', { date: date(q.validUntil, lang) }) : q.respondedAt ? T('req.quote.answered', { date: date(q.respondedAt, lang) }) : ''}
      </div>
      {live && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" variant="success" onClick={() => setAccepting(true)}>{T('req.quote.accept')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(true)}>{T('req.quote.reject')}</Button>
        </div>
      )}
      {accepting && <AcceptModal q={q} lang={lang} onClose={() => setAccepting(false)} onDone={() => { setAccepting(false); onChanged(); }} />}
      {rejecting && <RejectModal q={q} lang={lang} onClose={() => setRejecting(false)} onDone={() => { setRejecting(false); onChanged(); }} />}
    </div>
  );
}

function AcceptModal({ q, lang, onClose, onDone }) {
  const T = (k, v) => t(lang, k, v);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await dwisendRequestAPI.accept(q._id); toast.success(T('req.accept.done')); onDone(); }
    catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={T('req.accept.title', { ref: q.ref })} size="sm">
      <div className="space-y-3">
        <div className="text-sm">{T('req.accept.body', { amount: money(q.totalMinor, q.currency, lang) })}</div>
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>{T('req.accept.back')}</Button>
          <Button className="flex-1" variant="success" loading={busy} onClick={go}>{T('req.accept.confirm')}</Button>
        </div>
      </div>
    </Modal>
  );
}

function RejectModal({ q, lang, onClose, onDone }) {
  const T = (k, v) => t(lang, k, v);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await dwisendRequestAPI.reject(q._id, note); toast.success(T('req.reject.done')); onDone(); }
    catch (err) { toast.error(err.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={T('req.reject.title', { ref: q.ref })} size="sm">
      <div className="space-y-3">
        <Input label={T('req.reject.why')} value={note} onChange={e => setNote(e.target.value)} placeholder={T('req.reject.why.ph')} />
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={onClose}>{T('req.accept.back')}</Button>
          <Button className="flex-1" loading={busy} onClick={go}>{T('req.reject.confirm')}</Button>
        </div>
      </div>
    </Modal>
  );
}
